import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { fxGadgetFire } from '../render/weapon-fx';
import { disposeGadget, makeDrone, makeFieldRing, updateFieldRing } from '../render/gadgets';
import { damageEnemy, nearestEnemy, spawnRing } from '../sim/fx';
import { spawnBullet } from '../sim/weapons';
import { safeSpot } from '../world/arena';
import { P } from '../world/player';
import { mines } from '../world/pools';
import type { Evolution } from './evolutions';
/* ============================================================
   進化 · 第二批（下半）
   ============================================================

   跟 `evolutions-b.ts` 是同一批（2026-08-05 的覆蓋率補完），設計原則寫在
   `data/evolutions.ts`。**拆檔純粹是行數**：加上質變等級（`CRAFT_MAX = 3`）
   之後 `-b` 漲到 332 行，超過這個 repo 的單檔 300 行上限。

   分法照「動的是哪一種投射物」：`-b` 是刃幕／僚機／力場（持續型與慢速彈幕），
   這裡是全周彈幕／反射彈／黏雷（快速彈道與地雷池）—— 同一個池子的東西放在
   一起，配平時「這個池子總共餵得動幾發」才看得完。 */

export var EVOLUTIONS_C: Evolution[] = [
  {
    /* 後衛散布 + 分導彈頭 → 全周彈幕。
       **拿掉的限制：「你必須猜敵人從哪一側來」。**
       原本它只打背後（Lv4 之後前後），而這個遊戲的死法幾乎都是被包夾 ——
       它補的是自動瞄準造成的盲區，但它自己也有兩個盲區（左右）。
       進化之後**方向這個概念消失**：每次齊射覆蓋整個圓周，沒有背後可言。 */
    id: 'e-omni', n: '全周彈幕', kind: '進化', axes: ['範圍', '冷卻'],
    evo: { from: 'w-rear', needs: 'p-multi' },
    turn: 3, turnD: '每發打中也只咬一隻 → 貫穿力大增（打穿整條隊列）',
    d: function (lv) {
      var n = lv < 2 ? 12 : (lv < 3 ? 15 : 18);
      return '**沒有方向可言**：每次齊射朝整個圓周撒出 ' + n + ' 發彈丸，盲區消失'
        + (lv < 3 ? '' : '；**貫穿力大增，打穿整條隊列**');
    },
    cd: function () { return 0.75; },
    fire: function (lv) {
      /* ⚠️ 配平：單發 54 → **110**（第一版 ×1.56，最弱的一條）。
         **全向的代價是大部分彈丸打空**：12 發平均分給整個圓周，而敵人只佔其中幾個方向。
         來源朝背後打的三發全部落在敵群裡，所以「發數變多」在這裡不等於「輸出變多」。

         等級（2026-08-05）：Lv2 12 → 16 發（+33%）；Lv3 20 發，且**貫穿 1 → 4**——
         拿掉「打中也只咬一隻」這條限制，連成一線的隊形一發能貫穿到底。
         （20/12 ≈ 1.67x，貫穿加成未計入） */
      /* ⚠️ 2026-08-05 收斂：原本 `8 + lv*4`（12/16/20）＋ 貫穿 4，
         `pnpm crafts` 量到成長 ×2.20，超過 1.6–1.9× 帶寬上緣。
         質變 Lv1 已經是來源的 3.19 倍，×2.2 之後等於來源的七倍。 */
      var n = lv < 2 ? 12 : (lv < 3 ? 15 : 18), pierce = lv < 3 ? 1 : 3;
      var crit = RNG.next() < P.s.crit;
      for (var i = 0; i < n; i++) {
        /* 以 `faceAim` 當基準而不是世界零度：齊射會跟著機體轉，
           讀起來是「這台機體在旋轉噴射」而不是一個貼在地圖上的圖案。 */
        var a = P.faceAim + (i / n) * TAU;
        spawnBullet(P.pos.x, P.pos.z, a, 110 * (crit ? P.s.critMul : 1), crit, 46, pierce, 0, 0.5, undefined, 'e-omni');
      }
      spawnRing(P.pos.x, P.pos.z, 0xffd24a, 1.5, 7, 0.2);
      SFX.shoot();
    }
  },
  {
    /* 反射彈 + 臨界共振 → 無盡反射。**這一條直接對應 VS 的 NO FUTURE**
       （穿透上限移除）。
       **拿掉的限制：「反彈次數」與「三秒就消失」。**
       原本它滿級也只彈 6 次、活 3.4 秒，所以「貼著場邊打」的價值只成立幾秒鐘。
       進化之後彈道實質上不會停（12 次 / 6 秒），而且**每次反彈都分裂** ——
       場地邊界從「牆」變成「增幅器」。 */
    id: 'e-endless', n: '無盡反射', kind: '進化', axes: ['傷害', '冷卻'],
    evo: { from: 'w-ricochet', needs: 'p-crit' },
    turn: 3, turnD: '貫穿數固定 → 貫穿數提升（密集隊形一次咬穿更多）',
    d: function (lv) {
      return '**反彈不再有次數上限**：彈道在場內持續往返，而且每次反彈都分裂'
        + (lv < 2 ? '' : '，開火更密') + (lv < 3 ? '' : '；**貫穿數提升，一次咬穿更多目標**');
    },
    /* ⚠️ 2026-08-05 收斂：0.6/0.48/0.40 ＋ 單發 104 ＋ 貫穿 4 被新量測台量到
       成長 ×2.04（帶寬 1.6–1.9×）。反彈彈道會自我複製，每一個旋鈕的效果
       在這把武裝上都會被放大 —— 三個一起加就過頭了。 */
    cd: function (lv) { return lv < 2 ? 0.6 : (lv < 3 ? 0.50 : 0.44); },
    fire: function (lv) {
      /* 12 次而不是無限：子彈是池化的（`world/pools.ts`），
         真的寫無限的話一把武裝就能把池子吃光，別的武裝會靜默停止生成。
         12 次 × 6 秒在實戰裡撐得比整場遭遇還久，體感就是「不會停」。 */
      /* ⚠️ 這把武裝會自我複製（反彈 + 每次反彈都分裂），cd／傷害的變動都會被放大，
         所以等級刻意保守：
           Lv2  冷卻 0.6 → 0.48（+25%）
           Lv3  冷卻再降到 0.40、單發 96 → 104（+8%），且**貫穿 2 → 4**——
                拿掉「一次只能咬 2 隻」這條限制。
         名目倍率 1.5×1.08 ≈ 1.63x，貫穿加成沒算進去（怕被自我複製放大到失真）。 */
      var pierce = lv < 3 ? 2 : 3, dmg = 96;
      for (var i = 0; i < 2; i++) {
        var tgt = nearestEnemy(P.pos, 40, P.faceAim + (i ? 1.4 : 0), true);
        var a = tgt ? Math.atan2(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z) : P.faceAim + (i ? 1.4 : 0);
        var crit = RNG.next() < P.s.crit;
        var b = spawnBullet(P.pos.x, P.pos.z, a, dmg * (crit ? P.s.critMul : 1), crit, 50, pierce, 0, 0.5, undefined, 'e-endless');
        if (b) { b.bounce = 12; b.split = 1; b.ttl = 6; }
      }
      SFX.shoot();
    }
  },
  {
    /* 磁暴地雷 + 磁力場擴張 → 磁吸黏雷。**`w-mine` 的第二條路線。**
       **拿掉的限制：「地雷是靜態的，敵人不踩就不會爆」。**
       雷毯那條路線（磁暴雷場）是把這條限制**接受下來**再放大 ——
       腳下永遠有雷，代價是你得站在雷上面。這一條反過來：
       **雷直接吸附到敵人身上**，所以你可以一直往前跑，
       它從「陣地」變成「追擊」。兩條路線的站位要求正好相反。 */
    id: 'e-limpet', n: '磁吸黏雷', kind: '進化', axes: ['傷害', '衝擊'],
    evo: { from: 'w-mine', needs: 'p-magnet' },
    turn: 3, turnD: '爆炸半徑太窄，只炸中心那一隻 → 半徑大幅擴大（一顆炸一整群）',
    d: function (lv) {
      return '**雷不再等人踩**：磁吸到最近的三個目標身上引爆，腳下仍留兩顆守身'
        + (lv < 2 ? '' : '，單顆威力提升') + (lv < 3 ? '' : '；**爆炸半徑大幅擴大，一顆炸一整群**');
    },
    cd: function () { return 0.95; },
    fire: function (lv) {
      /* ⚠️ **冷卻與顆數刻意不隨等級調（見下）。** `mines` 池只有 18 顆
         （`world/pools.ts`），這條進化一次開火就吃掉 3 顆吸附 + 2 顆守身、
         各活 4／6 秒——用 Little's law 估，Lv1 常駐就已經逼近上限。
         再把冷卻壓短只會讓更多顆在池滿時被 `if (!mn) break` 靜靜丟掉，
         倍率量不出來反而更低。所以成長改走**不吃池子**的兩個軸：
         單顆威力、爆炸半徑（`areaDamage` 對半徑內每一隻都打一次，
         範圍變大＝同一顆炸中更多隻，不需要更多顆）。

         等級（2026-08-05）：
           Lv2  單顆 96 → 126（+31%）
           Lv3  單顆再到 140、且**爆炸半徑 ×1.3**——拿掉「命中範圍太窄，
                只炸得到中心那一隻」這條限制。
           （140/96 ≈ 1.46x，半徑加成未計入，避免落地時倍率被算過頭） */
      var ar = P.s.area || 1, dmg = lv < 2 ? 96 : (lv < 3 ? 126 : 140);
      var bmul = lv < 3 ? 1 : 1.3;
      /* 挑最近的三隻**不同**目標。`nearestEnemy` 每次都會回同一隻，
         所以這裡自己走一次並記住挑過的 —— 三顆全貼在同一個目標上的話，
         這一條就退化成「單體爆發」，跟雷毯那條的差異也跟著消失。 */
      var picked: any[] = [];
      for (var k = 0; k < 3; k++) {
        var best = null, bd = 34;
        for (var i = 0; i < enemies.length; i++) {
          var e = enemies[i];
          if (picked.indexOf(e) >= 0) continue;
          var d = Math.hypot(e.pos.x - P.pos.x, e.pos.z - P.pos.z);
          if (d < bd) { bd = d; best = e; }
        }
        if (!best) break;
        picked.push(best);
        var mn = mines.get(); if (!mn) break;
        /* 貼在目標**當下的位置**。雷本身不會跟著走（`sim/projectiles.ts` 的
           地雷沒有跟隨欄位），但 0.35 秒的充能時間短到敵人跑不出觸發半徑 ——
           讀起來就是「黏上去然後炸」。 */
        mn.m.position.set(best.pos.x, 0, best.pos.z); mn.m.scale.setScalar(0.01);
        mn.life = 0; mn.arm = 0.35; mn.dmg = dmg; mn.who = 'e-limpet';
        mn.trigR = 3.4 * ar;
        mn.blast = 7.2 * ar * bmul;
        mn.ttl = 4 * (P.s.dur || 1);
        scene.add(mn.m);
        /* **吸附的那一拍。** 這條進化跟雷毯那條的差別就是「雷會貼到敵人身上」，
           而地雷在敵人腳下出現是靜悄悄的 —— 沒有這一下，玩家分不出
           「雷剛貼上去」與「那裡本來就有一顆」。 */
        fxGadgetFire('e-limpet', best.pos.x, 1.2, best.pos.z, 0);
      }
      /* **腳下也要留兩顆。**（2026-08-05 第二輪，`pnpm metrics` 抓出來的。）
         第一版只貼敵人，於是這條進化把來源最重要的性質整個拿掉了：
         `w-mine` 的雷毯是**防禦**（貼上來的東西自己會炸），而全部貼到遠處
         等於腳下空了。存活中位數因此掉到 9.8 分，滯空佔比同步飆高
         （bot 一直在空中逃）—— **進化可以換掉玩法，但不能靜靜拿掉生存工具**。
         判準：改一條進化之前先問「來源武裝在防守什麼，新的還守不守得住」。
         數量固定不隨等級成長：這是存活底線，不是輸出軸。 */
      for (var f = 0; f < 2; f++) {
        var m2 = mines.get(); if (!m2) break;
        var fa = rr(0, TAU), fd = rr(3.5, 7);
        var sp = safeSpot(P.pos.x + Math.cos(fa) * fd, P.pos.z + Math.sin(fa) * fd, 1);
        m2.m.position.set(sp.x, 0, sp.z); m2.m.scale.setScalar(0.01);
        m2.life = 0; m2.arm = 0.3; m2.dmg = dmg; m2.who = 'e-limpet';
        m2.trigR = 6.2 * ar;
        m2.blast = 6.6 * ar * bmul;
        m2.ttl = 6 * (P.s.dur || 1);
        scene.add(m2.m);
      }
      SFX.beam();
    }
  }
];
